import { useMemo, useState, useEffect, useCallback } from "react";
import httpClient from "services/network/httpClient";
import { debounce } from "utility/Util";
import AsyncSelect from "react-select/async";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faCheck,
  faBuilding,
  faExclamationCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

interface AssignDistributorsProps {
  employee?: any;
  onClose?: () => void;
  onSuccess?: () => void;
}

interface DistributorOption {
  label: string;
  value: number;
  data?: any;
  isExisting?: boolean;
}

const AssignDistributors = ({ employee, onClose, onSuccess }: AssignDistributorsProps) => {
  const [selectedDistributors, setSelectedDistributors] = useState<DistributorOption[]>([]);
  const [existingDistributorIds, setExistingDistributorIds] = useState<number[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const loadExistingDistributors = useCallback(async () => {
    setInitialLoading(true);
    setError(null);

    try {
      const response: any = await httpClient.get(`/distributor/users/${employee.id}/distributors`);
      console.log("Existing distributors response:", response);

      const existingDistributors = (response.value || response || []).map((item: any) => ({
        label: item.distributorName,
        value: item.id,
        data: item,
        isExisting: true,
      }));

      setSelectedDistributors(existingDistributors);
      setExistingDistributorIds(existingDistributors.map((d: DistributorOption) => d.value));
    } catch (error) {
      console.error("Error loading existing distributors:", error);
      setError("Failed to load existing distributors");
    } finally {
      setInitialLoading(false);
    }
  }, [employee?.id]);

  // Load existing distributors on mount
  useEffect(() => {
    if (employee?.id) {
      loadExistingDistributors();
    }
  }, [employee?.id, loadExistingDistributors]);

  const searchedList = async (term: string) => {
    if (term.trim() === "" || term.length < 3) {
      return [];
    }
    try {
      const response: any = await httpClient.get(
        `distributor/search?term=${term}`
      );
      console.log("API Response for distributor search:", response);

      return response.value.contents.map((item: any) => ({
        label: item.distributorName,
        value: item.id,
        data: item,
        isExisting: false,
      }));
    } catch (error) {
      console.error("API Error:", error);
      return [];
    }
  };

  const debouncedLoadOptions = useMemo(() => {
    const load = (inputValue: string, callback: (options: any[]) => void) => {
      searchedList(inputValue).then(callback);
    };

    return debounce(load, 500);
  }, []);

  const handleAssign = async () => {
    setLoading(true);
    setError(null);

    try {
      // Current selected IDs
      const currentSelectedIds = selectedDistributors.map((d) => d.value);

      // Determine which were REMOVED (in existing but not in current) and which are NEW
      const oldDistributorIds = existingDistributorIds.filter(id => !currentSelectedIds.includes(id));
      const newDistributorIds = currentSelectedIds.filter(id => !existingDistributorIds.includes(id));

      console.log("Payload:", {
        oldDistributorIds: oldDistributorIds.length > 0 ? oldDistributorIds : [],
        newDistributorIds: newDistributorIds.length > 0 ? newDistributorIds : [],
      });

      await httpClient.put(`/distributor/users/${employee?.id}/distributors`, {
        oldDistributorIds: oldDistributorIds.length > 0 ? oldDistributorIds : [],
        newDistributorIds: newDistributorIds.length > 0 ? newDistributorIds : [],
      });

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 1500);
    } catch (error) {
      console.error("Error assigning distributors:", error);
      setError("Failed to update distributors. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: "48px",
      borderRadius: "0.75rem",
      borderColor: state.isFocused ? "#3B82F6" : "#E5E7EB",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: "#3B82F6",
      },
    }),
    menu: (base: any) => ({
      ...base,
      borderRadius: "0.75rem",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
    }),
    option: (base: any, state: any) => {
      let backgroundColor = "white";
      if (state.isSelected) {
        backgroundColor = "#3B82F6";
      } else if (state.isFocused) {
        backgroundColor = "#EFF6FF";
      }

      return {
        ...base,
        backgroundColor,
        color: state.isSelected ? "white" : "#1F2937",
        padding: "12px 16px",
        cursor: "pointer",
        "&:active": {
          backgroundColor: "#3B82F6",
        },
      };
    },
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "#EFF6FF",
      borderRadius: "0.5rem",
      padding: "2px 4px",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: "#1E40AF",
      fontWeight: "500",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: "#3B82F6",
      "&:hover": {
        backgroundColor: "#3B82F6",
        color: "white",
      },
      borderRadius: "0 0.5rem 0.5rem 0",
    }),
  };

  if (initialLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-5xl animate-spin mb-4"
        />
        <p className="text-gray-600 font-medium">Loading existing distributors...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center animate-fadeIn">
          <div className="bg-green-500 text-white p-2 rounded-full mr-3">
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <div>
            <p className="text-green-800 font-semibold">Success!</p>
            <p className="text-green-600 text-sm">Distributors updated successfully</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-center">
          <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 text-xl mr-3" />
          <p className="text-red-600 font-medium">{error}</p>
        </div>
      )}

      {/* Info Section */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-xl p-4 flex items-start">
        <FontAwesomeIcon icon={faBuilding} className="text-blue-600 text-lg mr-3 mt-1" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold mb-1">Managing Distributors for {employee?.firstName} {employee?.lastName}</p>
          <p className="text-blue-700">
            • Add new distributors by searching below<br />
            • Remove distributors by clicking the × button<br />
            • Click "Update Distributors" to save changes
          </p>
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-5 border border-blue-200 dark:border-blue-700">
        <div className="flex items-center mb-3">
          <div className="bg-blue-500 text-white p-2 rounded-lg mr-3">
            <FontAwesomeIcon icon={faSearch} />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 dark:text-gray-200">Add More Distributors</h3>
            <p className="text-sm text-gray-400">Type at least 3 characters to search</p>
          </div>
        </div>

        <AsyncSelect
          isMulti
          cacheOptions
          loadOptions={debouncedLoadOptions}
          defaultOptions={false}
          value={selectedDistributors}
          onChange={(selectedOptions) => {
            setSelectedDistributors(selectedOptions ? [...selectedOptions] : []);
            setError(null);
          }}
          placeholder="Start typing distributor name to add more..."
          isClearable
          styles={customSelectStyles}
          noOptionsMessage={({ inputValue }) =>
            inputValue.length < 3
              ? "Type at least 3 characters to search"
              : "No distributors found"
          }
          loadingMessage={() => "Searching..."}
        />
      </div>

      {/* Selected Distributors */}


      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={onClose}
          className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleAssign}
          disabled={loading}
          className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center shadow-md ${loading
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white hover:shadow-lg"
            }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
              Updating...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheck} className="mr-2" />
              Update Distributors
            </>
          )}
        </button>
      </div>
    </div>
  );
};
export default AssignDistributors;
